import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
  Upload, 
  X, 
  Image as ImageIcon,
  DollarSign,
  Loader2,
  CheckSquare,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

// Helper function to convert image paths to API endpoint
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL (OCI direct URL), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Handle vehicle images: "/uploads/vehicle_images/{vehicle_id}/{filename}.jpg"
  const vehicleImageMatch = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
  if (vehicleImageMatch) {
    return `${API}/images/${vehicleImageMatch[1]}/${vehicleImageMatch[2]}`;
  }
  
  // Handle additional images: "/uploads/additional_images/{vehicle_id}/{filename}.jpg"
  const additionalImageMatch = imagePath.match(/\/uploads\/additional_images\/([^/]+)\/([^/]+)/);
  if (additionalImageMatch) {
    return `${API}/additional-images/${additionalImageMatch[1]}/${additionalImageMatch[2]}`;
  }
  
  return `${BACKEND_URL}${imagePath}`; // Fallback to old format
};

const PREDEFINED_FEATURES = [
  { id: 'ac', label: 'A/C' },
  { id: 'power_steering', label: 'POWER STEERING' },
  { id: 'abs', label: 'ABS' },
  { id: 'airbag', label: 'AIRBAG' },
  { id: 'leather_seats', label: 'LEATHER SEATS' },
  { id: 'navigation', label: 'NAVIGATION' },
  { id: 'tv_navigation', label: 'T.V/ NAVIGATION' },
  { id: 'push_start', label: 'PUSH START' },
  { id: 'alloy_wheels', label: 'ALLOY WHEELS' },
  { id: 'fog_lights', label: 'FOG LIGHTS' },
  { id: 'body_kit', label: 'BODY KIT' }
];

const TRUCK_TYPES = [
  'Flatbody Truck',
  'Dump Truck',
  'Sprinkler Truck',
  'Water Tanker',
  'Cement Truck',
  'Crane Truck',
  'Freezer Truck'
];

const PublicListingTab = ({ vehicle, onUpdate, isEditing, setIsEditing, canEdit = true }) => {
  const [uploading, setUploading] = useState(false);
  const [viewingImage, setViewingImage] = useState(null); // For image viewer modal
  const [selectedImages, setSelectedImages] = useState([]); // For bulk delete
  const [deleting, setDeleting] = useState(false);
  
  // Collapsible sections state
  const [vehicleImagesOpen, setVehicleImagesOpen] = useState(true);
  const [additionalImagesOpen, setAdditionalImagesOpen] = useState(false);
  
  // Additional images state
  const [selectedAdditionalImages, setSelectedAdditionalImages] = useState([]);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [deletingAdditional, setDeletingAdditional] = useState(false);
  
  const [selectedFeatures, setSelectedFeatures] = useState({
    ac: false,
    power_steering: false,
    abs: false,
    airbag: false,
    leather_seats: false,
    navigation: false,
    tv_navigation: false,
    push_start: false,
    alloy_wheels: false,
    fog_lights: false,
    body_kit: false
  });
  const [additionalFeatures, setAdditionalFeatures] = useState('');
  const [truckTypeOtherMode, setTruckTypeOtherMode] = useState(
    () => Boolean(vehicle.truck_type && !TRUCK_TYPES.includes(vehicle.truck_type))
  );
  const [editData, setEditData] = useState({
    year: vehicle.year || '',
    make: vehicle.brand || '', // Renamed from brand to make
    model: vehicle.model || '',
    grade: vehicle.grade || '',
    auction_grade_entry: vehicle.auction_grade_entry || '',
    condition: vehicle.condition || 'Used',
    mileage: vehicle.mileage || '',
    fuel_type: vehicle.fuel_type || 'Gasoline',
    engine_cc: vehicle.engine_cc || '',
    transmission: vehicle.transmission || 'Automatic',
    drive_type: vehicle.drive_type || '',
    body_type: vehicle.body_type || vehicle.type || '',
    color: vehicle.color || '',
    doors: vehicle.doors || 4,
    seats: vehicle.seats || 5,
    car_dimension_m3: vehicle.freight_m3_unit || '',
    public_price_usd: vehicle.public_price_usd || '',
    public_price_jpy: vehicle.public_price_jpy || '',
    description: vehicle.description || '',
    // Truck-specific fields
    total_truck_weight: vehicle.total_truck_weight || '',
    total_loading_capacity: vehicle.total_loading_capacity || '',
    truck_type: vehicle.truck_type || '',
    truck_size: vehicle.truck_size || ''
  });

  useEffect(() => {
    if (isEditing) return;
    setEditData({
      year: vehicle.year || '',
      make: vehicle.brand || '',
      model: vehicle.model || '',
      grade: vehicle.grade || '',
      auction_grade_entry: vehicle.auction_grade_entry || '',
      condition: vehicle.condition || 'Used',
      mileage: vehicle.mileage || '',
      fuel_type: vehicle.fuel_type || 'Gasoline',
      engine_cc: vehicle.engine_cc || '',
      transmission: vehicle.transmission || 'Automatic',
      drive_type: vehicle.drive_type || '',
      body_type: vehicle.body_type || vehicle.type || '',
      color: vehicle.color || '',
      doors: vehicle.doors || 4,
      seats: vehicle.seats || 5,
      car_dimension_m3: vehicle.freight_m3_unit || '',
      public_price_usd: vehicle.public_price_usd || '',
      public_price_jpy: vehicle.public_price_jpy || '',
      description: vehicle.description || '',
      total_truck_weight: vehicle.total_truck_weight || '',
      total_loading_capacity: vehicle.total_loading_capacity || '',
      truck_type: vehicle.truck_type || '',
      truck_size: vehicle.truck_size || ''
    });
    setTruckTypeOtherMode(Boolean(vehicle.truck_type && !TRUCK_TYPES.includes(vehicle.truck_type)));
  }, [vehicle, isEditing]);

  // Parse existing features when component mounts or vehicle changes
  useEffect(() => {
    if (vehicle.features) {
      const featuresArray = vehicle.features.split(',').map(f => f.trim().toLowerCase());
      const newSelectedFeatures = { ...selectedFeatures };
      const extraFeatures = [];

      featuresArray.forEach(feature => {
        switch(feature) {
          case 'a/c':
            newSelectedFeatures.ac = true;
            break;
          case 'power steering':
            newSelectedFeatures.power_steering = true;
            break;
          case 'abs':
            newSelectedFeatures.abs = true;
            break;
          case 'airbag':
            newSelectedFeatures.airbag = true;
            break;
          case 'leather seats':
            newSelectedFeatures.leather_seats = true;
            break;
          case 'navigation':
            newSelectedFeatures.navigation = true;
            break;
          case 't.v/ navigation':
            newSelectedFeatures.tv_navigation = true;
            break;
          case 'push start':
            newSelectedFeatures.push_start = true;
            break;
          case 'alloy wheels':
            newSelectedFeatures.alloy_wheels = true;
            break;
          case 'fog lights':
            newSelectedFeatures.fog_lights = true;
            break;
          case 'body kit':
            newSelectedFeatures.body_kit = true;
            break;
          default:
            if (feature) extraFeatures.push(feature);
        }
      });

      setSelectedFeatures(newSelectedFeatures);
      setAdditionalFeatures(extraFeatures.join(', '));
    }
  }, [vehicle.features]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    // Validate all files before upload
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }

      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 20MB size limit`);
        return;
      }
    }

    setUploading(true);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          await axios.post(
            `${API}/vehicles/${vehicle.id}/images`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          successCount++;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} image(s) uploaded successfully!`);
        onUpdate(); // Refresh vehicle data
      }
      
      if (failCount > 0) {
        toast.error(`${failCount} image(s) failed to upload`);
      }
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/vehicles/${vehicle.id}/images?image_url=${encodeURIComponent(imageUrl)}`
      );
      toast.success('Image deleted successfully!');
      setSelectedImages([]); // Clear selection
      onUpdate();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.length === 0) {
      toast.error('No images selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedImages.length} image(s)?`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.post(
        `${API}/vehicles/${vehicle.id}/images/bulk-delete`,
        selectedImages
      );
      
      toast.success(`${response.data.deleted_count} image(s) deleted successfully!`);
      setSelectedImages([]); // Clear selection
      onUpdate();
    } catch (error) {
      console.error('Error deleting images:', error);
      toast.error('Failed to delete images');
    } finally {
      setDeleting(false);
    }
  };

  const handleImageReorder = async (result) => {
    if (!result.destination) return;

    const items = Array.from(images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Optimistically update UI
    try {
      await axios.put(
        `${API}/vehicles/${vehicle.id}/images/reorder`,
        items
      );
      toast.success('Images reordered! First image is now the thumbnail.');
      onUpdate();
    } catch (error) {
      console.error('Error reordering images:', error);
      toast.error('Failed to reorder images');
      onUpdate(); // Revert to original order
    }
  };

  const toggleImageSelection = (imageUrl) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(url => url !== imageUrl);
      } else {
        return [...prev, imageUrl];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages([...images]);
    }
  };

  // Additional Images Handlers
  const handleAdditionalImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingAdditional(true);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          await axios.post(`${API}/vehicles/${vehicle.id}/additional-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          successCount++;
        } catch (error) {
          console.error('Error uploading additional image:', error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} additional image(s) uploaded successfully!`);
        onUpdate();
      }

      if (failCount > 0) {
        toast.error(`${failCount} additional image(s) failed to upload`);
      }

      e.target.value = '';
    } catch (error) {
      console.error('Error uploading additional images:', error);
      toast.error('Failed to upload additional images');
    } finally {
      setUploadingAdditional(false);
    }
  };

  const handleDeleteAdditionalImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this additional image?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/vehicles/${vehicle.id}/additional-images?image_url=${encodeURIComponent(imageUrl)}`
      );
      toast.success('Additional image deleted successfully!');
      setSelectedAdditionalImages([]);
      onUpdate();
    } catch (error) {
      console.error('Error deleting additional image:', error);
      toast.error('Failed to delete additional image');
    }
  };

  const handleBulkDeleteAdditional = async () => {
    if (selectedAdditionalImages.length === 0) {
      toast.error('No additional images selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedAdditionalImages.length} additional image(s)?`)) {
      return;
    }

    setDeletingAdditional(true);
    try {
      const response = await axios.post(
        `${API}/vehicles/${vehicle.id}/additional-images/bulk-delete`,
        selectedAdditionalImages
      );

      toast.success(`${response.data.deleted_count} additional image(s) deleted successfully!`);
      setSelectedAdditionalImages([]);
      onUpdate();
    } catch (error) {
      console.error('Error deleting additional images:', error);
      toast.error('Failed to delete additional images');
    } finally {
      setDeletingAdditional(false);
    }
  };

  const handleAdditionalImageReorder = async (result) => {
    if (!result.destination) return;

    const items = Array.from(additionalImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    try {
      await axios.put(
        `${API}/vehicles/${vehicle.id}/additional-images/reorder`,
        items
      );
      toast.success('Additional images reordered!');
      onUpdate();
    } catch (error) {
      console.error('Error reordering additional images:', error);
      toast.error('Failed to reorder additional images');
      onUpdate();
    }
  };

  const toggleAdditionalImageSelection = (imageUrl) => {
    setSelectedAdditionalImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(url => url !== imageUrl);
      } else {
        return [...prev, imageUrl];
      }
    });
  };

  const toggleSelectAllAdditional = () => {
    if (selectedAdditionalImages.length === additionalImages.length) {
      setSelectedAdditionalImages([]);
    } else {
      setSelectedAdditionalImages([...additionalImages]);
    }
  };

  const handleFeatureToggle = (featureId) => {
    setSelectedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  };

  const combineFeatures = () => {
    const features = [];
    
    // Add selected predefined features (in CAPITAL LETTERS)
    if (selectedFeatures.ac) features.push('A/C');
    if (selectedFeatures.power_steering) features.push('POWER STEERING');
    if (selectedFeatures.abs) features.push('ABS');
    if (selectedFeatures.airbag) features.push('AIRBAG');
    if (selectedFeatures.leather_seats) features.push('LEATHER SEATS');
    if (selectedFeatures.navigation) features.push('NAVIGATION');
    if (selectedFeatures.tv_navigation) features.push('T.V/ NAVIGATION');
    if (selectedFeatures.push_start) features.push('PUSH START');
    if (selectedFeatures.alloy_wheels) features.push('ALLOY WHEELS');
    if (selectedFeatures.fog_lights) features.push('FOG LIGHTS');
    if (selectedFeatures.body_kit) features.push('BODY KIT');
    
    // Add additional features (comma-separated)
    if (additionalFeatures.trim()) {
      const additional = additionalFeatures
        .split(',')
        .map(f => f.trim())
        .filter(f => f.length > 0);
      features.push(...additional);
    }
    
    return features.join(', ');
  };

  const handleSave = async () => {
    try {
      // Convert numeric string fields to integers/floats
      const dataToSend = {
        ...editData,
        year: editData.year ? parseInt(editData.year) : null,
        brand: editData.make, // Map make to brand for backend
        grade: editData.grade,
        mileage: editData.mileage ? parseInt(editData.mileage) : null,
        engine_cc: editData.engine_cc ? parseInt(editData.engine_cc) : null,
        doors: editData.doors ? parseInt(editData.doors) : null,
        seats: editData.seats ? parseInt(editData.seats) : null,
        freight_m3_unit: editData.car_dimension_m3 ? parseFloat(editData.car_dimension_m3) : null,
        public_price_jpy: editData.public_price_jpy ? parseFloat(editData.public_price_jpy) : null,
        public_price_usd: editData.public_price_usd ? parseFloat(editData.public_price_usd) : null,
        features: combineFeatures()
      };
      
      // Remove make from the object since backend expects brand
      delete dataToSend.make;
      
      await axios.put(`${API}/vehicles/${vehicle.id}`, dataToSend);
      toast.success('Public listing details updated!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating vehicle:', error);
      const message = extractErrorMessage(error, 'Failed to update vehicle');
      toast.error(message);
    }
  };

  const images = vehicle.images || [];
  const additionalImages = vehicle.additional_images || [];

  return (
    <div className="space-y-4">
      {/* Vehicle Images Section - Collapsible */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => setVehicleImagesOpen(!vehicleImagesOpen)}
        >
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ImageIcon className="h-5 w-5" />
              <span>Vehicle Images</span>
              {images.length > 0 && (
                <span className="text-sm text-slate-500">({images.length})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {vehicleImagesOpen ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </div>
          </CardTitle>
        </CardHeader>
        {vehicleImagesOpen && (
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {canEdit && images.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectAll();
                      }}
                      className="text-xs"
                    >
                      <CheckSquare className="h-3 w-3 mr-1" />
                      {selectedImages.length === images.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedImages.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBulkDelete();
                        }}
                        disabled={deleting}
                        className="text-xs"
                      >
                        {deleting ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3 mr-1" />
                        )}
                        Delete ({selectedImages.length})
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Label 
                htmlFor="image-upload" 
                className={`cursor-pointer ${uploading ? 'opacity-50' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={uploading || !canEdit}
                  asChild
                >
                  <span>
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Images
                      </>
                    )}
                  </span>
                </Button>
                <Input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={!canEdit}
                />
              </Label>
            </div>
            {images.length > 0 ? (
            <>
              {canEdit && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 flex items-center gap-2">
                    <GripVertical className="h-4 w-4" />
                    <span>
                      <strong>Tip:</strong> Drag images to reorder them. The first image will be used as the thumbnail.
                    </span>
                  </p>
                </div>
              )}
              <DragDropContext onDragEnd={handleImageReorder}>
                <Droppable droppableId="images" direction="horizontal">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="grid grid-cols-2 md:grid-cols-3 gap-4"
                    >
                      {images.map((imageUrl, index) => (
                        <Draggable
                          key={imageUrl}
                          draggableId={imageUrl}
                          index={index}
                          isDragDisabled={!canEdit}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`relative group ${
                                snapshot.isDragging ? 'opacity-50' : ''
                              }`}
                            >
                              {/* Drag Handle */}
                              {canEdit && (
                                <div
                                  {...provided.dragHandleProps}
                                  className="absolute top-2 left-2 z-10 bg-white/90 p-1 rounded cursor-move opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <GripVertical className="h-4 w-4 text-slate-600" />
                                </div>
                              )}
                              
                              {/* Thumbnail Badge */}
                              {index === 0 && (
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                  Thumbnail
                                </div>
                              )}

                              {/* Selection Checkbox */}
                              {canEdit && (
                                <div className="absolute top-2 right-2 z-10">
                                  <Checkbox
                                    checked={selectedImages.includes(imageUrl)}
                                    onCheckedChange={() => toggleImageSelection(imageUrl)}
                                    className="bg-white/90"
                                  />
                                </div>
                              )}

                              {/* Image */}
                              <img
                                src={getImageUrl(imageUrl)}
                                alt={`Vehicle ${index + 1}`}
                                className={`w-full h-48 object-cover rounded-lg border-2 cursor-pointer hover:opacity-90 transition-opacity ${
                                  selectedImages.includes(imageUrl)
                                    ? 'border-blue-500 ring-2 ring-blue-300'
                                    : 'border-slate-200'
                                }`}
                                onClick={() => setViewingImage(getImageUrl(imageUrl))}
                              />

                              {/* Delete Button */}
                              {canEdit && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleDeleteImage(imageUrl)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          ) : (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No images uploaded yet</p>
              <Label htmlFor="image-upload">
                <Button variant="outline" asChild>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload First Image
                  </span>
                </Button>
              </Label>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Additional Images Section - Collapsible */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => setAdditionalImagesOpen(!additionalImagesOpen)}
        >
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ImageIcon className="h-5 w-5" />
              <span>Additional Images</span>
              {additionalImages.length > 0 && (
                <span className="text-sm text-slate-500">({additionalImages.length})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {additionalImagesOpen ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </div>
          </CardTitle>
        </CardHeader>
        {additionalImagesOpen && (
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {canEdit && additionalImages.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectAllAdditional();
                      }}
                      className="text-xs"
                    >
                      <CheckSquare className="h-3 w-3 mr-1" />
                      {selectedAdditionalImages.length === additionalImages.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedAdditionalImages.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBulkDeleteAdditional();
                        }}
                        disabled={deletingAdditional}
                        className="text-xs"
                      >
                        {deletingAdditional ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3 mr-1" />
                        )}
                        Delete ({selectedAdditionalImages.length})
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Label 
                htmlFor="additional-image-upload" 
                className={`cursor-pointer ${uploadingAdditional ? 'opacity-50' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={uploadingAdditional || !canEdit}
                  asChild
                >
                  <span>
                    {uploadingAdditional ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Images
                      </>
                    )}
                  </span>
                </Button>
                <Input
                  id="additional-image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAdditionalImageUpload}
                  className="hidden"
                  disabled={!canEdit}
                />
              </Label>
            </div>
            {additionalImages.length > 0 ? (
              <>
                {canEdit && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800 flex items-center gap-2">
                      <GripVertical className="h-4 w-4" />
                      <span>
                        <strong>Tip:</strong> Drag additional images to reorder them.
                      </span>
                    </p>
                  </div>
                )}
                <DragDropContext onDragEnd={handleAdditionalImageReorder}>
                  <Droppable droppableId="additional-images" direction="horizontal">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="grid grid-cols-2 md:grid-cols-3 gap-4"
                      >
                        {additionalImages.map((imageUrl, index) => (
                          <Draggable
                            key={imageUrl}
                            draggableId={imageUrl}
                            index={index}
                            isDragDisabled={!canEdit}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`relative group ${
                                  snapshot.isDragging ? 'opacity-50' : ''
                                }`}
                              >
                                {canEdit && (
                                  <div
                                    {...provided.dragHandleProps}
                                    className="absolute top-2 left-2 z-10 bg-white/90 p-1 rounded cursor-move opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <GripVertical className="h-4 w-4 text-slate-600" />
                                  </div>
                                )}

                                {canEdit && (
                                  <div className="absolute top-2 right-2 z-10">
                                    <Checkbox
                                      checked={selectedAdditionalImages.includes(imageUrl)}
                                      onCheckedChange={() => toggleAdditionalImageSelection(imageUrl)}
                                      className="bg-white/90"
                                    />
                                  </div>
                                )}

                                <img
                                  src={getImageUrl(imageUrl)}
                                  alt={`Additional ${index + 1}`}
                                  className={`w-full h-48 object-cover rounded-lg border-2 cursor-pointer hover:opacity-90 transition-opacity ${
                                    selectedAdditionalImages.includes(imageUrl)
                                      ? 'border-purple-500 ring-2 ring-purple-300'
                                      : 'border-slate-200'
                                  }`}
                                  onClick={() => setViewingImage(getImageUrl(imageUrl))}
                                />

                                {canEdit && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleDeleteAdditionalImage(imageUrl)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </>
            ) : (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No additional images uploaded yet</p>
                <Label htmlFor="additional-image-upload">
                  <Button variant="outline" asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload First Additional Image
                    </span>
                  </Button>
                </Label>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Public Listing Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5" />
              <span>Public Listing Details</span>
            </div>
            {canEdit && (
              !isEditing ? (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              ) : (
                <div className="space-x-2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    Save
                  </Button>
                </div>
              )
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="public_price_usd">PUBLIC PRICE (USD)</Label>
              {isEditing ? (
                <Input
                  id="public_price_usd"
                  type="number"
                  value={editData.public_price_usd}
                  onChange={(e) => setEditData({...editData, public_price_usd: e.target.value})}
                  placeholder="15000"
                />
              ) : (
                <p className="text-lg font-semibold text-green-600">
                  {vehicle.public_price_usd ? `$${vehicle.public_price_usd.toLocaleString()}` : 'Not set'}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="public_price_jpy">PUBLIC PRICE (JPY)</Label>
              {isEditing ? (
                <Input
                  id="public_price_jpy"
                  type="number"
                  value={editData.public_price_jpy}
                  onChange={(e) => setEditData({...editData, public_price_jpy: e.target.value})}
                  placeholder="2250000"
                />
              ) : (
                <p className="text-lg font-semibold text-green-600">
                  {vehicle.public_price_jpy ? `¥${vehicle.public_price_jpy.toLocaleString()}` : 'Not set'}
                </p>
              )}
            </div>
          </div>

          {/* Basic Specs */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label htmlFor="year">YEAR</Label>
              {isEditing ? (
                <Input
                  id="year"
                  type="number"
                  value={editData.year}
                  onChange={(e) => setEditData({...editData, year: e.target.value})}
                  placeholder="2020"
                />
              ) : (
                <p className="font-medium">{vehicle.year || 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="make">MAKE</Label>
              {isEditing ? (
                <Input
                  id="make"
                  value={editData.make}
                  onChange={(e) => setEditData({...editData, make: e.target.value.toUpperCase()})}
                  placeholder="TOYOTA"
                />
              ) : (
                <p className="font-medium">{vehicle.brand || 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="model">MODEL</Label>
              {isEditing ? (
                <Input
                  id="model"
                  value={editData.model}
                  onChange={(e) => setEditData({...editData, model: e.target.value.toUpperCase()})}
                  placeholder="CAMRY"
                />
              ) : (
                <p className="font-medium">{vehicle.model || 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="grade">GRADE</Label>
              {isEditing ? (
                <Input
                  id="grade"
                  value={editData.grade}
                  onChange={(e) => setEditData({...editData, grade: e.target.value.toUpperCase()})}
                  placeholder="4.5"
                />
              ) : (
                <p className="font-medium">{vehicle.grade || 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="auction_grade_entry">AUCTION GRADE ENTRY</Label>
              {isEditing ? (
                <Input
                  id="auction_grade_entry"
                  value={editData.auction_grade_entry}
                  onChange={(e) => setEditData({...editData, auction_grade_entry: e.target.value.toUpperCase()})}
                  placeholder="Enter auction grade entry"
                />
              ) : (
                <p className="font-medium">{vehicle.auction_grade_entry || 'Not set'}</p>
              )}
            </div>
          </div>

          {/* Mileage and Other Specs */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="mileage">MILEAGE</Label>
              {isEditing ? (
                <Input
                  id="mileage"
                  type="number"
                  value={editData.mileage}
                  onChange={(e) => setEditData({...editData, mileage: e.target.value})}
                  placeholder="45000"
                />
              ) : (
                <p className="font-medium">{vehicle.mileage ? `${vehicle.mileage.toLocaleString()} KM` : 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="car_dimension_m3">CAR DIMENSION M3</Label>
              {isEditing ? (
                <Input
                  id="car_dimension_m3"
                  type="number"
                  step="0.001"
                  value={editData.car_dimension_m3}
                  onChange={(e) => setEditData({...editData, car_dimension_m3: e.target.value})}
                  placeholder="12.345"
                />
              ) : (
                <p className="font-medium">{vehicle.freight_m3_unit || vehicle.freight_m3_unit === 0 ? `${vehicle.freight_m3_unit} m3` : 'Not set'}</p>
              )}
            </div>
          </div>

          {/* Engine & Transmission */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="fuel_type">FUEL TYPE</Label>
              {isEditing ? (
                <Select 
                  value={editData.fuel_type} 
                  onValueChange={(value) => setEditData({...editData, fuel_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Gasoline">Gasoline</SelectItem>
                    <SelectItem value="Diesel">Diesel</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                    <SelectItem value="Electric">Electric</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium">{vehicle.fuel_type || 'Gasoline'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="engine_cc">ENGINE (CC)</Label>
              {isEditing ? (
                <Input
                  id="engine_cc"
                  type="number"
                  value={editData.engine_cc}
                  onChange={(e) => setEditData({...editData, engine_cc: e.target.value})}
                  placeholder="2500"
                />
              ) : (
                <p className="font-medium">{vehicle.engine_cc ? `${vehicle.engine_cc} CC` : 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="transmission">TRANSMISSION</Label>
              {isEditing ? (
                <Select 
                  value={editData.transmission} 
                  onValueChange={(value) => setEditData({...editData, transmission: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Automatic">Automatic</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="CVT">CVT</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium">{vehicle.transmission || 'Automatic'}</p>
              )}
            </div>
          </div>

          {/* Body Details */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label htmlFor="body_type">BODY TYPE</Label>
              {isEditing ? (
                <Input
                  id="body_type"
                  value={editData.body_type}
                  onChange={(e) => setEditData({...editData, body_type: e.target.value.toUpperCase()})}
                  placeholder="Sedan"
                />
              ) : (
                <p className="font-medium">{vehicle.body_type || vehicle.type}</p>
              )}
            </div>
            <div>
              <Label htmlFor="color">COLOR</Label>
              {isEditing ? (
                <Input
                  id="color"
                  value={editData.color}
                  onChange={(e) => setEditData({...editData, color: e.target.value.toUpperCase()})}
                  placeholder="Black"
                />
              ) : (
                <p className="font-medium">{vehicle.color || 'Not set'}</p>
              )}
            </div>
            <div>
              <Label htmlFor="doors">DOORS</Label>
              {isEditing ? (
                <Input
                  id="doors"
                  type="number"
                  value={editData.doors}
                  onChange={(e) => setEditData({...editData, doors: e.target.value})}
                  placeholder="4"
                />
              ) : (
                <p className="font-medium">{vehicle.doors || 4}</p>
              )}
            </div>
            <div>
              <Label htmlFor="seats">SEATS</Label>
              {isEditing ? (
                <Input
                  id="seats"
                  type="number"
                  value={editData.seats}
                  onChange={(e) => setEditData({...editData, seats: e.target.value})}
                  placeholder="5"
                />
              ) : (
                <p className="font-medium">{vehicle.seats || 5}</p>
              )}
            </div>
          </div>

          {/* Drive Type */}
          <div>
            <Label htmlFor="drive_type">DRIVE TYPE</Label>
            {isEditing ? (
              <Select 
                value={editData.drive_type} 
                onValueChange={(value) => setEditData({...editData, drive_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select drive type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2WD">2WD</SelectItem>
                  <SelectItem value="4WD">4WD</SelectItem>
                  <SelectItem value="AWD">AWD</SelectItem>
                  <SelectItem value="FWD">FWD</SelectItem>
                  <SelectItem value="RWD">RWD</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="font-medium">{vehicle.drive_type || 'Not set'}</p>
            )}
          </div>

          {/* Truck-Specific Fields - Only show when body_type is "Truck" */}
          {(isEditing ? editData.body_type : vehicle.body_type)?.toLowerCase() === 'truck' && (
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span>🚚</span>
                <span>Truck-Specific Details</span>
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Total Truck Weight */}
                <div>
                  <Label htmlFor="total_truck_weight">TOTAL TRUCK WEIGHT</Label>
                  {isEditing ? (
                    <Input
                      id="total_truck_weight"
                      value={editData.total_truck_weight || ''}
                      onChange={(e) => setEditData({...editData, total_truck_weight: e.target.value.toUpperCase()})}
                      placeholder="e.g., 3.5 Ton, 5 Ton"
                    />
                  ) : (
                    <p className="font-medium">{vehicle.total_truck_weight || 'Not set'}</p>
                  )}
                </div>

                {/* Total Loading Capacity */}
                <div>
                  <Label htmlFor="total_loading_capacity">TOTAL LOADING CAPACITY</Label>
                  {isEditing ? (
                    <Input
                      id="total_loading_capacity"
                      value={editData.total_loading_capacity || ''}
                      onChange={(e) => setEditData({...editData, total_loading_capacity: e.target.value.toUpperCase()})}
                      placeholder="e.g., 2 Ton, 4 Ton"
                    />
                  ) : (
                    <p className="font-medium">{vehicle.total_loading_capacity || 'Not set'}</p>
                  )}
                </div>

                {/* Truck Type */}
                <div>
                  <Label htmlFor="truck_type">TRUCK TYPE</Label>
                  {isEditing ? (
                    <>
                      <Select 
                        value={truckTypeOtherMode || (editData.truck_type && !TRUCK_TYPES.includes(editData.truck_type)) ? '__other__' : (editData.truck_type || '')}
                        onValueChange={(value) => {
                          if (value === '__other__') {
                            setTruckTypeOtherMode(true);
                          } else {
                            setTruckTypeOtherMode(false);
                            setEditData({...editData, truck_type: value});
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select truck type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TRUCK_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                          <SelectItem value="__other__">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {(truckTypeOtherMode || (editData.truck_type && !TRUCK_TYPES.includes(editData.truck_type))) && (
                        <Input
                      id="truck_type_other"
                      value={editData.truck_type}
                      onChange={(e) => setEditData({...editData, truck_type: e.target.value.toUpperCase()})}
                          placeholder="Enter truck type"
                          className="mt-2"
                        />
                      )}
                    </>
                  ) : (
                    <p className="font-medium">{vehicle.truck_type || 'Not set'}</p>
                  )}
                </div>

                {/* Truck Size */}
                <div>
                  <Label htmlFor="truck_size">TRUCK SIZE</Label>
                  {isEditing ? (
                    <Select 
                      value={editData.truck_size || ''} 
                      onValueChange={(value) => setEditData({...editData, truck_size: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select truck size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Small Size (upto 1.5 Ton)">Small Size (upto 1.5 Ton)</SelectItem>
                        <SelectItem value="Medium Size (Upto 3 Ton)">Medium Size (Upto 3 Ton)</SelectItem>
                        <SelectItem value="Large Size (Over 3.5 Ton)">Large Size (Over 3.5 Ton)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">{vehicle.truck_size || 'Not set'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label htmlFor="description">DESCRIPTION</Label>
            {isEditing ? (
              <Textarea
                id="description"
                value={editData.description}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
                placeholder="Well-maintained vehicle with full service history..."
                rows={4}
              />
            ) : (
              <p className="text-sm text-slate-600 whitespace-pre-wrap">
                {vehicle.description || 'No description'}
              </p>
            )}
          </div>

          {/* Features */}
          <div>
            <Label className="flex items-center gap-2 mb-3">
              <CheckSquare className="h-4 w-4" />
              VEHICLE FEATURES
            </Label>
            {isEditing ? (
              <div className="space-y-4">
                {/* Predefined Features Checkboxes */}
                <Card className="bg-slate-50 border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-slate-700">Common Features</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {PREDEFINED_FEATURES.map(feature => (
                        <div key={feature.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={feature.id}
                            checked={selectedFeatures[feature.id]}
                            onCheckedChange={() => handleFeatureToggle(feature.id)}
                          />
                          <Label 
                            htmlFor={feature.id} 
                            className="text-sm font-medium cursor-pointer"
                          >
                            {feature.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Additional Features Input */}
                <div>
                  <Label htmlFor="additional_features" className="text-sm text-slate-700">
                    Additional Features (comma-separated)
                  </Label>
                  <Textarea
                    id="additional_features"
                    value={additionalFeatures}
                    onChange={(e) => setAdditionalFeatures(e.target.value)}
                    placeholder="e.g., Sunroof, Parking Sensors, Cruise Control"
                    rows={2}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Add any other features not listed above, separated by commas
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {vehicle.features ? (
                  vehicle.features.split(',').map((feature, idx) => (
                    <span 
                      key={`feature-${idx}-${vehicle.id}`} 
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {feature.trim()}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No features listed</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Image Viewer Modal */}
      <Dialog open={!!viewingImage} onOpenChange={() => setViewingImage(null)}>
        <DialogContent className="max-w-4xl">
          <div className="relative">
            <img
              src={viewingImage}
              alt="Vehicle"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
              onClick={() => setViewingImage(null)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicListingTab;
